/**
 * Unified ParkingMapView Component
 *
 * This component consolidates native and web versions of the ParkingMapView.
 * It renders a map view with parking locations, clustering, and floating action buttons (FABs)
 * for navigation and map reset. Supports detailed callouts for parking locations.
 *
 * Features:
 * - Interactive map with markers and clustering.
 * - FABs for navigation and resetting the map.
 * - Detailed callouts for parking locations.
 *
 * @module ParkingMapView
 */
import React from "react";
import { useNavigation } from "@react-navigation/native";
import { Platform } from "react-native";
import { parkingFormatAddress, toTitleCase } from "../../utils/formatting";
import GeneralMapView from "../Other/MapView/GeneralMapView";
import ParkingStore from "../../../src/storage/parkingStore";
import { getMarkerIcon } from "../../utils/helper";
import { logAnalyticsEvent } from "../../utils/firebase";

const ParkingMapView = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-ParkingMapView" });

  const navigation = useNavigation();

  const initialCenter =
    Platform.OS === "web"
      ? { lat: 36.8506, lng: -75.9779 }
      : {
          latitude: 36.77,
          longitude: -75.99,
          latitudeDelta: 0.28,
          longitudeDelta: 0.28,
        };

  const parkingData = ParkingStore((state) => state.parking);

  const handleNavigateToListView = () => {
    navigation.navigate("ParkingListView");
  };

  const handleNavigateToDetailView = (location) => {
    const index = parkingData.findIndex((parking) => parking.id === location.id);
    navigation.navigate("ParkingDetailsView", { index });
  };

  const calloutProps = {
    title: (location) => toTitleCase(location.name),
    subtitle: (location) => parkingFormatAddress(location.address),
  };

  const getIconsForLocation = (location) => {
    const type = location?.type || "default";
    const color = location?.statusColor;
    return {
      enabled: getMarkerIcon(type, color, "enabled"),
      disabled: getMarkerIcon(type, color, "disabled"),
    };
  };

  return (
    <>
      <GeneralMapView
        data={parkingData}
        initialCenter={initialCenter}
        markerIcons={getIconsForLocation}
        calloutProps={calloutProps}
        onNavToListView={handleNavigateToListView}
        onNavToDetailView={handleNavigateToDetailView}
      />
    </>
  );
};

export default ParkingMapView;
